import { AxiosRequestConfig, AxiosResponse } from 'axios'
import Pai, { ResponseStatusTypes } from './Pai'
import ApiModule from '@no-996/axios-api'
import options from '@/api/options'
import Api from '@/api'

/**
 * process注入
 */
declare const process: Pai.process
declare global {
  interface Window {
    __MICRO_APP_ENVIRONMENT__: boolean
    microApp: {
      /**
       * 子应用向基座应用发送数据
       * @param data 来自基座应用的数据
       */
      dispatch(data: any): void
      /**
       * 绑定监听函数
       * @param dataListener 监听函数
       * @param autoTrigger 在初次绑定监听函数时如果有缓存数据，是否需要主动触发一次，默认为false
       */
      addDataListener(dataListener: Function, autoTrigger?: boolean): void
      /**
       * 解绑监听函数
       * @param dataListener 监听函数
       */
      removeDataListener(dataListener: Function): void
      /**
       * 清空当前子应用的所有绑定函数(全局数据函数除外)
       */
      clearDataListener(): void
    }
  }
}

const tokenKeyDefault = 'st'
let paiAuthLog = false
const msgStatus = {
  // 死循环检测
  MAX_ITERATIONS: 150,
  MAX_INTERVAL: 4,
  lastDate: Date.now(),
  loopCount: 0,
}

/**
 * 混淆
 * @param token 混淆前
 * @returns 混淆后
 */
const encrypt = (token: string) => {
  try {
    return window.btoa(token).split('').reverse().join('')
  } catch (e) {
    console.error(e)
    return ''
  }
}

/**
 * 解混淆
 * @param token 混淆后
 * @returns 混淆前
 */
const decrypt = (tokenEncrypted: string) => {
  try {
    return window.atob(tokenEncrypted.split('').reverse().join(''))
  } catch (e) {
    console.error(e)
    return ''
  }
}

/**
 * 基类
 */
class AuthApiBase {
  protected readonly options: Pai.AuthApiOptions
  //
  constructor(opts: Pai.AuthApiOptions) {
    this.options = opts
  }
  // 清除url登录信息
  loginInfoClear(url: string) {
    let keys = this.options?.redirectQueryClearKeys || []
    let clear = url
    keys.forEach((o) => {
      // 'http://localhost:82/pai-ui/home?st=1EGZmVmN2ATOmRzYtI2NiFWL0ADN00yY1ImMtgjY5AzNkRGM&a=1&b=2'.replace(new RegExp(`\\?st=[^&]*(&?)`, 'g'), '?')
      // -> 'http://localhost:82/pai-ui/home?a=1&b=2'
      // 'http://localhost:82/pai-ui/home?st=1EGZmVmN2ATOmRzYtI2NiFWL0ADN00yY1ImMtgjY5AzNkRGM'.replace(new RegExp(`\\?st=[^&]*(&?)`, 'g'), '?')
      // -> 'http://localhost:82/pai-ui/home'
      clear = clear
        .replace(new RegExp(`\\?${o}=[^&]*(&?)`, 'g'), '?')
        .replace(/\?$/, '')
      // 'http://localhost:82/pai-ui/home?a=1&b=2&st=1EGZmVmN2ATOmRzYtI2NiFWL0ADN00yY1ImMtgjY5AzNkRGM&c=3'.replace(new RegExp(`\\?st=[^&]*(&?)`, 'g'), '$1').replace(new RegExp(`&st=[^&]*(&?)`, 'g'), '$1')
      // -> 'http://localhost:82/pai-ui/home?a=1&b=2&c=3'
      clear = clear.replace(new RegExp(`&${o}=[^&]*(&?)`, 'g'), '$1')
    })
    return clear.replace(/[&?]$/, '')
  }
  // 登录跳转
  loginRedirect(redirectUrl?: string) {
    let url = redirectUrl || this.loginInfoClear(window.location.href)
    const loginUrl = `${/^https?:\/\//.test(this.options?.loginPath || '')
      ? ''
      : this.options.origin || window.location.origin
      }${this.options?.loginPath}/?type=${this.options?.type || ''}`
    // 重登录地址
    window.location.href =
      loginUrl +
      (this.options?.redirect ? `&url=${encodeURIComponent(url)}` : '')
  }
  // 存储登录信息
  setTokenInfo(params: Pai.LoginInfo) {
    if (params && params.st) {
      this.options?.cacheStorage?.setItem(
        this.options?.cacheKey || '',
        JSON.stringify({
          st: params.st,
          t: Date.now(),
          v: process.env.PackageJSON.version,
        }),
      )
    }
  }
}

/**
 * 顶层类
 */
class AuthApiTop extends AuthApiBase implements Pai.AuthApiInterface {
  // 请求
  private readonly api: typeof Api | ApiModule
  //
  constructor(opts: Pai.AuthApiOptions) {
    super(opts)
    //
    this.loginFromUrl()
    //
    this.api = new ApiModule(
      // 接口定义
      options,
      // axios配置
      {
        baseURL: this.options.baseURL,
      },
      // axios-api配置
      {
        interceptors: {
          request: {
            onFulfilled: (value) => {
              return value
            },
            onRejected: (error) => { },
          },
          response: {
            onFulfilled: (value) => {
              let { data } = value
              let { code } = data
              if (code) {
                if (code.toString() !== '200') {
                  return Promise.reject(new Error(code.toString()))
                } else {
                  return data.data
                }
              }
              return data
            },
            onRejected: (error) => { },
          },
        },
      },
    )
  }

  // 从url获取登录信息
  loginFromUrl() {
    let params: Pai.LoginInfo = {}
    let reg = '([^&=?]+)=([^&]*)'
    let regEach = new RegExp(reg)
    let sets = window.location.href.match(new RegExp(reg, 'g'))
    if (sets) {
      sets.forEach((o) => {
        let m = o.match(regEach)
        if (m) {
          let [, key, value] = m
          if (key === this.options?.tokenKey) {
            params.st = decodeURIComponent(value)
          }
        }
      })
      this.setTokenInfo(params)
      //
      if (this.options?.redirectQueryClear) {
        let url = this.loginInfoClear(window.location.href)
        if (window.location.href !== url) {
          window.location.replace(url)
        }
      }
    }
  }
  // 获取登录信息
  getLoginInfo(): Pai.LoginInfo | null {
    try {
      const cache = this.options?.cacheStorage?.getItem(
        this.options?.cacheKey || '',
      )
      if (cache) {
        const params = JSON.parse(cache)
        return params
      }
      return null
    } catch (e) {
      paiAuthLog ? console.error(e) : ''
      return null
    }
  }
  async login(opts?: Pai.LogoutOptions) {
    let confirm = false
    if (!(opts instanceof Event) && opts?.confirm && opts?.confirmCallback) {
      try {
        confirm = await opts?.confirmCallback()
      } catch (e) {
        confirm = false
      }
    } else {
      confirm = true
    }
    if (confirm) {
      this.loginRedirect(opts?.redirectUrl)
    }
    return confirm
  }
  async logout(opts?: Pai.LogoutOptions) {
    let confirm = false
    if (!(opts instanceof Event) && opts?.confirm && opts?.confirmCallback) {
      try {
        confirm = await opts?.confirmCallback()
      } catch (e) {
        confirm = false
      }
    } else {
      confirm = true
    }
    if (confirm) {
      let token = await this.getToken({ ...opts, stay: true })
      if (token) {
        try {
          await this.api.logout.request({
            headers: {
              Authorization: `Bearer ${token}`,
            },
          })
        } catch (e) {
          paiAuthLog ? console.error(e) : ''
        }
      }
      this.options?.cacheStorage?.removeItem(this.options?.cacheKey || '')
      if (!(opts instanceof Event) && !opts?.stay) {
        await this.login({
          confirm: false,
          confirmCallback: () => Promise.resolve(true),
          stay: false,
          redirectUrl: opts?.redirectUrl,
        })
      }
    }
    return confirm
  }
  async getToken(opts?: Pai.GetTokenOptions) {
    let st = this.getLoginInfo()?.st || ''
    if (!st) {
      if (!(opts instanceof Event) && !opts?.stay) {
        await this.login(opts)
      }
    }
    let token = ''
    if (this.options?.obfuscate) {
      try {
        token = decrypt(st?.toString() || '')
      } catch (e) {
        paiAuthLog ? console.error(e) : ''
      }
    } else {
      token = st
    }

    return Promise.resolve(token)
  }
  async getUserInfo(opts?: Pai.GetUserInfoOptions) {
    const token = await this.getToken(opts)
    try {
      let res =
        this.options?.type === 'developer'
          ? await this.api.userInfoDeveloper.request({
            headers: token
              ? {
                Authorization: `Bearer ${await this.getToken()}`,
              }
              : undefined,
          })
          : await this.api.userInfoWorkbench.request({
            headers: token
              ? {
                Authorization: `Bearer ${await this.getToken()}`,
              }
              : undefined,
          })
      return Promise.resolve(res)
    } catch (e) {
      paiAuthLog ? console.error(e) : ''
      if ((e as Error).message == '401') {
        if (!(opts instanceof Event) && !opts?.stay) {
          await this.login(opts)
        }
      }
      return Promise.resolve(null)
    }
  }
}

/**
 * 框架类
 */
class AuthApiIframe extends AuthApiBase implements Pai.AuthApiInterface {
  constructor(opts: Pai.AuthApiOptions) {
    super(opts)
    // 订阅工作台消息
    window.addEventListener('message', this.message, false)
  }
  message(e: MessageEvent) {
    try {
      let {
        data: { module, type, detail },
      } = e
      if (module === 'auth') {
        let event = new Event(`pai-${module}-${type}`) as any
        event.data = e.data
        window.dispatchEvent(event)
      }
    } catch (e) {
      paiAuthLog ? console.error(e) : ''
    }
  }
  callTop(module: string, type: string): Promise<any> {
    //
    // let { lastDate, MAX_INTERVAL, loopCount, MAX_ITERATIONS } = msgStatus
    // msgStatus.loopCount++
    // if (
    //   Date.now() - lastDate <= MAX_INTERVAL &&
    //   loopCount % MAX_ITERATIONS === 0
    // ) {
    //   msgStatus.MAX_ITERATIONS = 100
    //   msgStatus.loopCount = 0
    //   throw new Error('似乎进入了循环...')
    // } else {
    //   msgStatus.lastDate = Date.now()
    // }
    //
    checkLog()
    let name = `pai-${module}-${type}`
    return new Promise((resolve, reject) => {
      let timer = 0
      // 接收内部通知
      const listener = (e: Event) => {
        const {
          data: { detail },
        } = e as MessageEvent
        window.removeEventListener(name, listener)
        if (timer) {
          clearTimeout(timer)
        }
        resolve(detail)
      }
      timer = setTimeout(() => {
        window.removeEventListener(name, listener)
        reject(`工作台响应超时（${module}/${type}）`)
      }, 10000)
      // 订阅内部通知
      window.addEventListener(name, listener, { once: true })
      // 超时取消订阅内部通知

      // 向工作台请求
      window.parent.postMessage(
        {
          module,
          type,
        },
        '*',
      )
    })
  }
  //
  async login(opts?: Pai.ConfirmOptions) {
    if (opts && !(opts instanceof Event)) {
      paiAuthLog ? console.warn('在工作台中运行微应用，将忽略调用opts配置值。') : ''
    }
    return this.callTop('auth', 'login')
  }
  async logout(opts?: Pai.LogoutOptions) {
    if (opts && !(opts instanceof Event)) {
      paiAuthLog ? console.warn('在工作台中运行微应用，将忽略调用opts配置值。') : ''
    }
    return this.callTop('auth', 'logout')
  }
  async getToken(opts?: Pai.GetTokenOptions) {
    if (opts && !(opts instanceof Event)) {
      paiAuthLog ? console.warn('在工作台中运行微应用，将忽略调用opts配置值。', window.location.href) : ''
    }
    return this.callTop('auth', 'getToken')
  }
  async getUserInfo(opts?: Pai.GetUserInfoOptions) {
    if (opts && !(opts instanceof Event)) {
      paiAuthLog ? console.warn('在工作台中运行微应用，将忽略调用opts配置值。') : ''
    }
    return this.callTop('auth', 'getUserInfo')
  }
}

/**
 * 微前端
 */
class AuthApiMicro extends AuthApiBase implements Pai.AuthApiInterface {
  constructor(opts: Pai.AuthApiOptions) {
    super(opts)
  }
  callMicro(module: string, type: string): Promise<any> {
    return new Promise((resolve, reject) => {
      let timer = 0
      // 接收内部通知
      const listener = (detail: any) => {
        if (detail.module === module && detail.type === type) {
          window.microApp.removeDataListener(listener)
          if (timer) {
            clearTimeout(timer)
          }
          resolve(detail.data)
        }
      }
      timer = setTimeout(() => {
        window.microApp.removeDataListener(listener)
        reject(`微前端宿主响应超时（${module}/${type}）`)
      }, 10000)
      // 订阅内部通知
      window.microApp.addDataListener(listener, true)
      // 超时取消订阅内部通知
      // 向微前端宿主请求
      window.microApp.dispatch({
        module,
        type,
      })
    })
  }
  //
  async login(opts?: Pai.ConfirmOptions) {
    if (opts && !(opts instanceof Event)) {
      paiAuthLog ? console.warn('在微前端子应用中调用，将忽略调用opts配置值。') : ''
    }
    return this.callMicro('auth', 'login')
  }
  async logout(opts?: Pai.LogoutOptions) {
    if (opts && !(opts instanceof Event)) {
      paiAuthLog ? console.warn('在微前端子应用中调用，将忽略调用opts配置值。') : ''
    }
    return this.callMicro('auth', 'logout')
  }
  async getToken(opts?: Pai.GetTokenOptions) {
    if (opts && !(opts instanceof Event)) {
      paiAuthLog ? console.warn('在微前端子应用中调用，将忽略调用opts配置值。') : ''
    }
    return this.callMicro('auth', 'getToken')
  }
  async getUserInfo(opts?: Pai.GetUserInfoOptions) {
    if (opts && !(opts instanceof Event)) {
      paiAuthLog ? console.warn('在微前端子应用中调用，将忽略调用opts配置值。') : ''
    }
    return this.callMicro('auth', 'getUserInfo')
  }
}

/**
 * 授权工具
 */
class AuthApi implements Pai.AuthApiInterface, Pai.AuthInterceptorsInterface {
  // 是浏览器
  private readonly isClient = typeof window !== 'undefined'
  /**
   * 工具配置
   */
  readonly options: Pai.AuthApiOptions
  // 实例
  private instance: Pai.AuthApiInterface
  //
  constructor(opts: Pai.AuthApiOptions) {
    console.log(
      '%c%s%c%s',
      'color:#fff;background:blue;padding:2px 4px;border-radius:4px 0 0 4px;',
      process.env.PackageJSON.name,
      'color:#fff;background:green;padding:2px 4px;border-radius:0 4px 4px 0;',
      process.env.PackageJSON.version,
    )
    if (this.isClient) {
      // 配置覆盖
      this.options = Object.assign(
        {
          origin: '',
          baseURL: '',
          redirectQueryClear: true,
          redirectQueryClearKeys: [tokenKeyDefault],
          cacheKey: this.cacheKeyGen(),
          type: 'workbench',
          cacheStorage: localStorage,
          obfuscate: true,
          tokenKey: tokenKeyDefault,
          loginPath: '/auth',
          redirect: true,
          checkAuthKeyByUrl: 'paiAuthByUrl', // 是否接收的key，当url包含该key时，开启url认证优先
          // paiAuthByUrl: true, // 是否开启url认证优先
        },
        opts,
      )

      checkLog()
      let href = window.location.href || ''
      let hasAuthKey = href.includes(`?${this.options?.tokenKey}=`) || href.includes(`&${this.options?.tokenKey}=`)
      if (window.__MICRO_APP_ENVIRONMENT__) {
        // 微前端
        this.instance = new AuthApiMicro(this.options)
      } else if (window.self === window.top || href.includes(this.options.checkAuthKeyByUrl) || this.options.paiAuthByUrl) {
        // 顶层
        this.instance = new AuthApiTop(this.options)
      } else {
        // 微应用
        this.instance = new AuthApiIframe(this.options)
      }
    } else {
      throw new Error('auth-api仅能在浏览器客户端中运行')
    }
  }
  // 按规则生成cacheKey
  private cacheKeyGen() {
    let after = 'unknown'
    const features = window.location.href.match(
      /(https?):\/\/([^\/]+)\/([^\/?#]+)?/,
    )
    if (features) {
      if (features[3]) {
        after = features[3]
      }
    }
    return (
      'pai_' +
      process.env.PackageJSON.name.match(/@flyriselink\/pai-(auth)-api/)[1] +
      '_' +
      after
    )
  }
  private sleep(sec: number): Promise<void> {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve()
      }, sec)
    })
  }
  // api
  login(opts?: Pai.ConfirmOptions) {
    return this.instance?.login(opts) || false
  }
  logout(opts?: Pai.LogoutOptions) {
    return this.instance?.logout(opts) || false
  }
  async getToken(opts?: Pai.GetTokenOptions) {
    let res = ''
    try {
      res = (await this.instance?.getToken(opts)) || ''
    } catch (error) {
      this.instance = new AuthApiTop(this.options)
      res = (await this.instance?.getToken(opts)) || ''
    }
    return res
  }
  async getUserInfo(opts?: Pai.GetUserInfoOptions) {
    let res = await this.instance?.getUserInfo(opts)
    return res
  }
  //
  async requestHandler(req: AxiosRequestConfig): Promise<void> {
    const token = await this.getToken()
    if (token) {
      if (!req.headers) {
        req.headers = {}
      }
      req.headers.Authorization = 'Bearer ' + token
    }
  }
  async responseHandler(
    res: AxiosResponse,
    opts?: Pai.ConfirmOptions,
  ): Promise<ResponseStatusTypes> {
    if (res.data?.code?.toString() === '401') {
      await this.login(opts)
      return ResponseStatusTypes.Expires
    }
    return ResponseStatusTypes.Pass
  }
}

const checkLog = () => {
  if (!window) {
    return;
  }

  try {
    let topWin: any = window.top;
    const keyName: string = '$paiAuthLog';

    let openLog: boolean = (window as any)[keyName] || (topWin as any)[keyName] ? true : false;
    paiAuthLog = openLog
  } catch (error) {
    // console.log(error)
  }
}

export default AuthApi

export { ResponseStatusTypes, encrypt, decrypt }
